import template from 'url-template';
import axios from 'axios';
import { ConcurrencyManager } from 'axios-concurrency';

import {
    FETCH_CAMPUS_SITES,
    FETCH_CORE_AREAS,
    FETCH_CORE_CLIENTS,
    FETCH_CORE_CONTACTS,
    FETCH_CORE_EQUIPMENT,
    FETCH_CORE_SITES,
    FETCH_CORE_SYSTEMS,
    FETCH_SYSTEMS_BY_SITE,
    SET_CORE_CLIENT,
    SET_CORE_EQUIPMENT,
    SET_CORE_SITE,
    SET_CORE_SYSTEM
} from '../actions/actionTypes';

import _ from 'lodash';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';

import { setBobLoading } from './bobActions';
import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';

const AreaUrl = template.parse('/sites/{siteId}/areas');
const LinkEquipmentAreaUrl = template.parse('/areas/{areaId}/equipment/{equipmentId}');
const LinkEquipmentSystemUrl = template.parse('/systems/{systemId}/equipment/{equipmentId}');
const SystemUrl = template.parse('/sites/{siteId}/systems');
const CreateEquipmentUrl = template.parse('/sites/{siteId}/equipment{?page,limit,sort,searchFilter,extendedData}');
const EquipmentByIdUrl = template.parse('/equipment/{equipmentId}{?extendedData}');
const ContactsUrl = template.parse('/clients/{clientId}/contacts');
const EquipmentByClientUrl = template.parse('/clients/{clientId}/equipment{?page,limit,sort,searchFilter,extendedData}');
const ClientByIdUrl = template.parse('/clients/{clientId}');
const SiteByIdUrl = template.parse('/sites/{siteId}');
const CreateSiteUrl = template.parse('/clients/{clientId}/sites{?searchFilter,limit,page}');
const ClientCampusUrl = template.parse('/clients/{clientId}/campus{?searchFilter,limit}');
const CampusUrl = template.parse('/clients/{clientId}/campus-sites');
const CampusSiteUrl = template.parse('/campus/{campusId}/sites/{siteId}');
const CompaniesUrl = template.parse('/companies/{companyId}/clients{?searchFilter,limit,page}{&sort}');
const ClientsUrl = template.parse('/companies/{companyId}/clients{?page,limit,sort,searchFilter}');
const EquipmentUrl = template.parse('/companies/{companyId}/equipment{?page,limit,sort,searchFilter,extendedData,equipmentTypeId}');
const SitesUrl = template.parse('/companies/{companyId}/sites{?page,limit,sort,searchFilter}');
const ClientSitesUrl = template.parse('/clients/{clientId}/sites{?searchFilter,limit,campusId}');
const FetchSystemsUrl = template.parse('/sites/{siteId}/systems{?page,limit,sort,searchFilter}');
const SystemByIdUrl = template.parse('/systems/{systemId}');
const SensorsUrl = template.parse('/sites/{siteId}/sensors{?parentId,limit,page}');

const DEFAULT_PAGE_LIMIT = 1000;
const MAX_CONCURRENCY_REQUESTS = 10;

export const createCoreArea = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            AreaUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createCoreCampus = (clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            ClientCampusUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        const payload = _.get(response, 'data', '');

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createCoreEquipment = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            CreateEquipmentUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        const payload = _.get(response, 'data', '');

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createCoreSystem = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            SystemUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        const payload = _.get(response, 'data', '');

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createCoreClient = (companyId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            CompaniesUrl.expand({
                companyId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Client created successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createCoreSite = (clientId, body, campusId = '', options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const site = await k2aBaseApi.post(
            CreateSiteUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        if (campusId !== '') {
            const siteId = _.get(site, 'data.id', '');
            await k2aBaseApi.post(
                CampusSiteUrl.expand({
                    campusId,
                    siteId,
                    ...options
                }),
                body,
                header
            );
            await dispatch(fetchCampusSites(clientId, campusId));
        }

        dispatch(enqueueSnackbar('Site created successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCoreClient = (clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            ClientByIdUrl.expand({
                clientId
            }),
            header
        );

        dispatch(enqueueSnackbar('Client deleted successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCoreSite = (siteId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            SiteByIdUrl.expand({
                siteId
            }),
            header
        );

        dispatch(enqueueSnackbar('Site deleted successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCoreEquipment = (equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            EquipmentByIdUrl.expand({
                equipmentId
            }),
            header
        );

        dispatch(enqueueSnackbar('Equipment deleted successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCoreSystem = (systemId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            SystemByIdUrl.expand({
                systemId
            }),
            header
        );

        dispatch(enqueueSnackbar('System deleted successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchCoreAreas = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            AreaUrl.expand({
                siteId
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_CORE_AREAS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreClients = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;

    let payload = [];
    let meta = { isActive: true, company: companyId };

    dispatch(setBobLoading('FetchCoreClients'));

    try {
        const response = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );
        
        payload = _.get(response, 'data.data', []);
        payload = _.orderBy(payload, [item => item.name.toLowerCase()], ['asc']);

        payload.forEach(item => {
            if (!_.isEmpty(item.customAttributes)) {
                item.customAttributes.forEach((attr) => {
                    item[attr.customAvp.attributeName] = attr.customAvp.attributeValue;
                    item[`${attr.customAvp.attributeName}AttributeId`] = attr.customAvp.attributeId;
                    item[`${attr.customAvp.attributeName}AvpId`] = attr.customAvp.avpId;
                });
            }
        });

        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };

        dispatch({
            type:    FETCH_CORE_CLIENTS,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreClients'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_CLIENTS,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreClients'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchClients = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreClientById = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCoreClients'));

    try {
        const response = await k2aBaseApi.get(
            ClientByIdUrl.expand({
                clientId
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        dispatch(setCoreClient(payload));
        dispatch(setBobLoading('FetchCoreClients'));
    } catch (error) {
        dispatch(setBobLoading('FetchCoreClients'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCampusSites = (clientId, campusId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.campusId = campusId;
    try {
        const response = await k2aBaseApi.get(
            ClientSitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type:    FETCH_CAMPUS_SITES,
            payload: payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};


export const fetchCampuses = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            CampusUrl.expand({
                clientId
            }),
            header
        );

        const payload = _.get(response, 'data.campusList', []);
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreContacts = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            ContactsUrl.expand({
                clientId,
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_CORE_CONTACTS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreSites = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;
    options.page = 1;

    let url = SitesUrl.expand({
        companyId,
        ...options
    });

    let payload = [];
    let meta = { isActive: true, entity: 'company', company: companyId };

    dispatch(setBobLoading('FetchCoreSites'));

    try {
        const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
        const response = await k2aBaseApi.get(
            url,
            header
        );

        const updatedMeta = _.get(response, 'data.meta', {});
        const data = _.get(response, 'data.data', []);
        const total = updatedMeta.totalCount;

        let totalPages = total / options.limit;
        let count;
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i <= count; i++) {
            url = url.replace(`page=${i - 1}`, `page=${i}`);
            promises.push(k2aBaseApi.get(
                url,
                header
            ));
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();
        payload = _.orderBy(data.concat(...results), [item => item.name.toLowerCase()], ['asc']);
        
        meta = { ...meta, ...updatedMeta };

        dispatch({
            type:    FETCH_CORE_SITES,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreSites'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_SITES,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreSites'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreSitesByClient = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;

    let payload = [];
    let meta = { isActive: true, entity: 'client', client: clientId };

    dispatch(setBobLoading('FetchCoreSites'));

    try {
        const response = await k2aBaseApi.get(
            CreateSiteUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        payload = _.get(response, 'data.data', []);
        payload = _.orderBy(payload, [item => item.name.toLowerCase()], ['asc']);
        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };

        dispatch({
            type:    FETCH_CORE_SITES,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreSites'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_SITES,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreSites'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchSites = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;

    try {
        const response = await k2aBaseApi.get(
            CreateSiteUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreSiteById = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCoreSites'));

    try {
        const response = await k2aBaseApi.get(
            SiteByIdUrl.expand({
                siteId
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        dispatch(setCoreSite(payload));
        dispatch(setBobLoading('FetchCoreSites'));
        return payload;
    } catch (error) {
        dispatch(setBobLoading('FetchCoreSites'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreEquipment = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.extendedData = true;
    options.limit = 1000;

    let payload = [];
    let data = [];
    let meta = { isActive: true, entity: 'company', company: companyId };

    dispatch(setBobLoading('FetchCoreEquipment'));

    try {
        const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
        const response = await k2aBaseApi.get(
            EquipmentUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        data = _.get(response, 'data.data', []);
        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };
        const total = meta.totalCount;

        let totalPages = total / options.limit;
        let count;

        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i < count; i++) {
            options.page = i;
            promises.push(k2aBaseApi.get(
                EquipmentUrl.expand({
                    companyId,
                    ...options
                }),
                header
            ));
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();

        payload = _.orderBy(data.concat(...results), [item => item.name.toLowerCase()], ['asc']);
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreEquipmentByClient = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.extendedData = true;
    options.limit = 1000;

    let payload = [];
    let data = [];
    let meta = { isActive: true, entity: 'client', client: clientId };

    dispatch(setBobLoading('FetchCoreEquipment'));

    try {
        const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
        const response = await k2aBaseApi.get(
            EquipmentByClientUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        data = _.get(response, 'data.data', []);
        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };
        const total = meta.totalCount;

        let totalPages = total / options.limit;
        let count;

        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i < count; i++) {
            options.page = i;
            promises.push(k2aBaseApi.get(
                EquipmentByClientUrl.expand({
                    clientId,
                    ...options
                }),
                header
            ));
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();

        payload = _.orderBy(data.concat(...results), [item => item.name.toLowerCase()], ['asc']);
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreEquipmentBySite = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.extendedData = true;
    options.limit = 1000;

    let payload = [];
    let data = [];
    let meta = { isActive: true, entity: 'site', site: siteId };

    dispatch(setBobLoading('FetchCoreEquipment'));

    try {
        const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
        const response = await k2aBaseApi.get(
            CreateEquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        data = _.get(response, 'data.data', []);
        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };
        const total = meta.totalCount;

        let totalPages = total / options.limit;
        let count;

        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i < count; i++) {
            options.page = i;
            promises.push(k2aBaseApi.get(
                CreateEquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                header
            ));
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();

        payload = _.orderBy(data.concat(...results), [item => item.name.toLowerCase()], ['asc']);
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
    } catch (error) {
        dispatch({
            type:    FETCH_CORE_EQUIPMENT,
            payload: {
                data: payload,
                meta
            }
        });
        dispatch(setBobLoading('FetchCoreEquipment'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreEquipmentById = (equipmentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.extendedData = true;

    dispatch(setBobLoading('FetchCoreEquipment'));

    try {
        const response = await k2aBaseApi.get(
            EquipmentByIdUrl.expand({
                equipmentId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        dispatch(setCoreEquipment(payload));
        dispatch(setBobLoading('FetchCoreEquipment'));
        return payload;
    } catch (error) {
        dispatch(setBobLoading('FetchCoreEquipment'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentBySiteId = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;

    try {
        const response = await k2aBaseApi.get(
            CreateEquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreSystems = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit = 1000;

    let meta = { site: siteId };

    try {
        const response = await k2aBaseApi.get(
            FetchSystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        let payload = _.get(response, 'data.data', []);
        payload = _.orderBy(payload, [item => item.name.toLowerCase()], ['asc']);
        const updatedMeta = _.get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };

        dispatch({
            type:    FETCH_CORE_SYSTEMS,
            payload: {
                data: payload,
                meta
            }
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCoreSystemById = (systemId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCoreSystem'));

    try {
        const response = await k2aBaseApi.get(
            SystemByIdUrl.expand({
                systemId
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        dispatch(setCoreSystem(payload[0]));
        dispatch(setBobLoading('FetchCoreSystem'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('FetchCoreSystem'));
    }
};

export const fetchSystems = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            FetchSystemsUrl.expand({
                siteId
            }),
            header
        );

        let payload = _.get(response, 'data.data', []);
        payload = _.orderBy(payload, [item => item.name.toLowerCase()], ['asc']);

        dispatch({
            type: FETCH_SYSTEMS_BY_SITE,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSensorsByParentId = (siteId, parentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            SensorsUrl.expand({
                siteId,
                parentId
            }),
            header
        );

        let payload = _.get(response, 'data.data', []);
        
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const linkEquipmentToArea = (areaId, equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            LinkEquipmentAreaUrl.expand({
                areaId,
                equipmentId
            }),
            {},
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const unlinkEquipmentToArea = (areaId, equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            LinkEquipmentAreaUrl.expand({
                areaId,
                equipmentId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const linkEquipmentToSystem = (systemId, equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            LinkEquipmentSystemUrl.expand({
                systemId,
                equipmentId
            }),
            {},
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const unlinkEquipmentToSystem = (systemId, equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            LinkEquipmentSystemUrl.expand({
                systemId,
                equipmentId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateCoreClient = (clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            ClientByIdUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Client updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateCoreSite = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            SiteByIdUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Site updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateCoreEquipment = (equipmentId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            EquipmentByIdUrl.expand({
                equipmentId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Equipment updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateCoreSystem = (systemId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            SystemByIdUrl.expand({
                systemId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('System updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const setCoreClient = client => async (dispatch, getState) => {
    dispatch({
        type:    SET_CORE_CLIENT,
        payload: client
    });
};

export const setCoreClients = (clients, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_CORE_CLIENTS,
        payload: {
            data: clients,
            meta
        }
    });
};

export const setCoreSite = site => async (dispatch, getState) => {
    dispatch({
        type:    SET_CORE_SITE,
        payload: site
    });
};

export const setCoreSites = (sites, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_CORE_SITES,
        payload: {
            data: sites,
            meta
        }
    });
};

export const setCoreEquipment = equipment => async (dispatch, getState) => {
    dispatch({
        type:    SET_CORE_EQUIPMENT,
        payload: equipment
    });
};

export const setCoreEquipments = (equipment, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_CORE_EQUIPMENT,
        payload: {
            data: equipment,
            meta
        }
    });
};

export const setCoreSystem = system => async (dispatch, getState) => {
    dispatch({
        type:    SET_CORE_SYSTEM,
        payload: system
    });
};

export const setCoreSystems = (systems, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_CORE_SYSTEMS,
        payload: {
            data: systems,
            meta
        }
    });
};
